import React, { useEffect, useRef, useState } from 'react'

// Piezas para que las cifras "se sientan": números que cuentan hasta su valor, un
// anillo de progreso y una línea de tendencia. Compartidas por el resumen del
// listado de Vales y por Finanzas / Gastos.

const reducirMovimiento = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/** Valor que sube desde el anterior hasta `objetivo` en ~700 ms (ease-out). */
export function useCountUp(objetivo, duracion = 700) {
  const meta = Number.isFinite(Number(objetivo)) ? Number(objetivo) : 0
  const [valor, setValor] = useState(meta)
  const previo = useRef(0)

  useEffect(() => {
    if (reducirMovimiento()) { setValor(meta); previo.current = meta; return undefined }
    const desde = previo.current
    const t0 = performance.now()
    let id
    const paso = (ahora) => {
      const p = Math.min(1, (ahora - t0) / duracion)
      const suave = 1 - Math.pow(1 - p, 3)
      setValor(desde + (meta - desde) * suave)
      if (p < 1) id = requestAnimationFrame(paso)
      else previo.current = meta
    }
    id = requestAnimationFrame(paso)
    // Si la pestaña no pinta (oculta, ventana tapada) rAF se pausa: el cierre garantiza
    // que el número final quede bien aunque la animación no haya corrido.
    const cierre = setTimeout(() => { setValor(meta); previo.current = meta }, duracion + 150)
    return () => { cancelAnimationFrame(id); clearTimeout(cierre) }
  }, [meta, duracion])

  return valor
}

/** Número animado. `formato` recibe el valor entero en curso. */
export function CifraAnimada({ valor, formato = (n) => n.toLocaleString('es-AR'), className }) {
  const v = useCountUp(valor)
  return <span className={className}>{formato(Math.round(v))}</span>
}

/** Anillo de progreso con el porcentaje al centro. */
export function Anillo({ porcentaje = 0, size = 76, grosor = 8, color = '#10b981', pista = '#e2e8f0', children }) {
  const p = Math.max(0, Math.min(100, porcentaje))
  const r = (size - grosor) / 2
  const c = 2 * Math.PI * r
  const animado = useCountUp(p, 900)
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} role="img" aria-label={`${Math.round(p)} por ciento`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={pista} strokeWidth={grosor} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={grosor} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - animado / 100)} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-sm font-black tabular-nums text-ink">
        {children ?? `${Math.round(animado)}%`}
      </div>
    </div>
  )
}

/** Línea de tendencia con relleno suave. `datos` es una lista de números. */
export function Tendencia({ datos = [], color = '#16a34a', ancho = 120, alto = 40, relleno = true }) {
  if (!datos || datos.length < 2) return null
  const max = Math.max(...datos)
  const min = Math.min(...datos)
  const rango = max - min || 1
  const pts = datos.map((v, i) => [(i / (datos.length - 1)) * ancho, alto - 4 - ((v - min) / rango) * (alto - 8)])
  const linea = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const area = `0,${alto} ${linea} ${ancho},${alto}`
  return (
    <svg width={ancho} height={alto} viewBox={`0 0 ${ancho} ${alto}`} className="shrink-0" aria-hidden="true">
      {relleno && <polygon points={area} fill={color} opacity="0.14" />}
      <polyline points={linea} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="3" fill={color} />
    </svg>
  )
}
